/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Spin, Tooltip } from 'antd';
import { ButtonProps as AntButtonProps } from 'antd/es/button';
import React, { FC, MouseEventHandler, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Company } from 'api/models';
import { StatusFieldOption, StatusGroup } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { AutoAllocate16 } from 'components/atoms/icons/auto-allocate';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { PartnershipIcon } from 'components/organisms/resource-management/components';
import { NameCell as CommonNameCell } from 'components/organisms/timesheets/common/cells/name-cell';
import { SingleLineEllipsis } from 'components/styles';

const ActionsContainer = styled.span`
  position: absolute;
  right: 0;
`;

const Wrapper = styled.div<{
  $numberOfPrimaryButtons: number;
  $hasMoreActions: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  > div {
    width: calc(
      100% -
        ${({ $numberOfPrimaryButtons, $hasMoreActions }) =>
          `${$numberOfPrimaryButtons * 32 + ($hasMoreActions ? 44 : 0)}px`}
    );
  }
  ${ifProp(
    'onClick',
    css`
      cursor: pointer;
    `,
  )};
`;

export const TextWrapperWithEllipsis = styled.div`
  ${SingleLineEllipsis};
`;

export const SecondaryTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

const StyledDiv = styled.div`
  width: 100%;
`;

interface ButtonProps extends Omit<AntButtonProps, 'size' | 'type'> {
  tooltip?: ReactNode;
}
interface CellWrapperProps {
  dataCy?: string;
  dataCyValue?: string;
  menu?: React.ReactElement;
  primaryButtonsProps?: ButtonProps[];
  onMenuVisibleChange?: (visible: boolean) => void;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

export const CellWrapper: FC<CellWrapperProps> = ({
  dataCy,
  dataCyValue,
  menu,
  onMenuVisibleChange,
  onClick,
  children,
  primaryButtonsProps,
}) => {
  return (
    <Wrapper
      onClick={onClick}
      $numberOfPrimaryButtons={primaryButtonsProps?.length ?? 0}
      $hasMoreActions={menu != null}
    >
      <StyledDiv>{children}</StyledDiv>
      <ActionsContainer
        className="actions-container"
        data-cy={dataCy}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {primaryButtonsProps != null
          ? primaryButtonsProps.map((props) => (
              <Tooltip title={props.tooltip}>
                <span>
                  <Button
                    data-cy={dataCy}
                    size="small"
                    className="primary-action"
                    type="text"
                    hideHoverBorderRadius
                    {...props}
                  />
                </span>
              </Tooltip>
            ))
          : null}
        {menu != null ? (
          <MoreActionsDropdown
            dataCyValue={dataCyValue}
            className="cell-actions"
            menu={menu}
            onVisibleChange={onMenuVisibleChange}
            hideHoverBorderRadius
          />
        ) : null}
      </ActionsContainer>
    </Wrapper>
  );
};

export const StatusIndicatorWrapper = styled.span<{ $enlargeR: boolean }>`
  svg {
    width: 24px;
    height: 24px;
    ${({ $enlargeR }) =>
      $enlargeR
        ? css`
            circle {
              r: 9;
            }
          `
        : ''};
  }
`;
export const ProjectStatusIndicator = ({
  status,
}: {
  status: StatusFieldOption;
}): JSX.Element => (
  <StatusIndicatorWrapper $enlargeR={status.group !== StatusGroup.InProgress}>
    <StatusIndicator status={status} />
  </StatusIndicatorWrapper>
);

export const SpinnerWrapper = styled(Spin)<{
  $noRightSpace?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  ${({ $noRightSpace }) => ($noRightSpace ? '' : 'right: var(--spacing-05)')};
`;

export const StyledCSSSpinner = styled.div`
  width: 16px !important;
  height: 16px !important;
  border: 2px solid var(--primary-color);
  border-top-color: transparent;
  border-radius: 50%;
  animation: loading 1s linear infinite;

  @keyframes loading {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const AutoAllocateIcon = styled(AutoAllocate16)`
  color: var(--gray-70);
`;

export const NameCell = styled(CommonNameCell).attrs({
  primaryTextEllipsis: false,
})``;

export const ItemDelete = styled(Menu.ItemDelete)`
  align-items: flex-start !important;
  .ant-dropdown-menu-item-icon {
    flex-shrink: 0;
    margin-top: var(--spacing-01);
  }
  > span {
    white-space: normal;
  }
`;
const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

interface Props {
  name: string;
  company?: Company;
  showCompanyInfo: boolean;
}
export const NameWithCompanyInfo: FC<Props> = ({
  name,
  company,
  showCompanyInfo,
}) => (
  <NameWrapper>
    <TextWrapperWithEllipsis>{name}</TextWrapperWithEllipsis>
    {showCompanyInfo && company != null ? (
      <Tooltip title={company.companyName}>
        <PartnershipIcon />
      </Tooltip>
    ) : null}
  </NameWrapper>
);
